import React, { useState, useEffect } from 'react';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, Pencil, Trash2, Award, Loader2, Flame, PartyPopper, Star, Calendar, Target, Trophy,
  Droplets, Drumstick, Dumbbell, CandyOff, Footprints, Moon, ChevronUp, ChevronDown, 
  Medal, Heart, Zap, Brain, Ribbon, Rocket
} from 'lucide-react';
import { CELEBRATION_GIFS } from '../../components/GifCelebrationModal';
import { triggerCelebration } from '../../components/Celebration';

// Milestone type options
const MILESTONE_TYPES = [
  { value: 'action_streak', label: '🔥 Action Streak', description: 'X consecutive days of a specific action', icon: Flame },
  { value: 'perfect_day', label: '⭐ Full Send Day', description: 'Complete all 10 actions in one day', icon: Star },
  { value: 'checkin_streak', label: '📅 Check-in Streak', description: 'Check in X consecutive days (at least 1 point)', icon: Calendar },
  { value: 'monthly_points', label: '🎯 Monthly Points', description: 'Reach X total points in the month', icon: Target },
  { value: 'perfect_month', label: '🏆 Perfect Month', description: 'All 280 points in a month!', icon: Trophy },
  { value: 'monthly_kickoff', label: '🚀 Monthly Kickoff', description: 'First point collected each month', icon: Rocket },
  { value: 'comeback', label: '💪 Welcome Back', description: 'Return after 3+ days of inactivity', icon: Dumbbell },
];

// Badge icon options - expanded list
const BADGE_ICONS = [
  { value: 'droplets', label: '💧 Water Drop', icon: Droplets },
  { value: 'drumstick', label: '🍗 Drumstick (Protein)', icon: Drumstick },
  { value: 'dumbbell', label: '💪 Dumbbell (Strength)', icon: Dumbbell },
  { value: 'candy-off', label: '🚫🍬 No Candy (Sugar-free)', icon: CandyOff },
  { value: 'footprints', label: '👣 Footprints (Walking/NEAT)', icon: Footprints },
  { value: 'moon', label: '🌙 Moon (Sleep)', icon: Moon },
  { value: 'ribbon', label: '🎀 Ribbon (Achievement)', icon: Ribbon },
  { value: 'trophy', label: '🏆 Trophy', icon: Trophy },
  { value: 'award', label: '🏅 Award Medal', icon: Award },
  { value: 'medal', label: '🥇 Medal', icon: Medal },
  { value: 'star', label: '⭐ Star', icon: Star },
  { value: 'flame', label: '🔥 Flame (Streak)', icon: Flame },
  { value: 'heart', label: '❤️ Heart (Health)', icon: Heart },
  { value: 'zap', label: '⚡ Lightning (Energy)', icon: Zap },
  { value: 'brain', label: '🧠 Brain (Mental)', icon: Brain },
  { value: 'target', label: '🎯 Target (Goals)', icon: Target },
  { value: 'rocket', label: '🚀 Rocket (Kickoff)', icon: Rocket },
];

// Celebration type options
const CELEBRATION_TYPES = [
  { value: 'confetti', label: '🎊 Confetti Burst', description: 'Classic confetti explosion' },
  { value: 'fireworks', label: '🎆 Fireworks', description: 'Colorful fireworks display' },
  { value: 'stars', label: '⭐ Stars', description: 'Shooting stars effect' },
  { value: 'emoji', label: '🎉 Emoji Party', description: 'Fun emojis flying around' },
  { value: 'rainbow', label: '🌈 Rainbow', description: 'Rainbow-colored confetti' },
  { value: 'gif', label: '🖼️ Custom GIF', description: 'Show a celebratory GIF' },
];

const AdminMilestones = () => {
  const [milestones, setMilestones] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);
  
  const [form, setForm] = useState({
    milestone_type: 'action_streak',
    action_id: '',
    streak_days: 7,
    points_threshold: 50,
    badge_name: '',
    badge_icon: 'award',
    celebration_message: '',
    health_benefit: '',
    celebration_type: 'confetti',
    celebration_gif_url: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [milestonesRes, actionsRes] = await Promise.all([
        apiClient.get('/milestones'),
        apiClient.get('/actions')
      ]);
      setMilestones(milestonesRes.data);
      setActions(actionsRes.data);
    } catch (err) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const moveMilestone = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= milestones.length) return;
    
    // Create new array with swapped positions
    const newMilestones = [...milestones];
    [newMilestones[index], newMilestones[newIndex]] = [newMilestones[newIndex], newMilestones[index]];
    
    // Optimistically update UI
    setMilestones(newMilestones);
    setReordering(true);
    
    try {
      // Send new order to backend
      const order = newMilestones.map(m => m.id);
      await apiClient.post('/milestones/reorder', order);
      toast.success('Order updated');
    } catch (err) {
      toast.error('Failed to reorder');
      // Revert on error
      fetchData();
    } finally {
      setReordering(false);
    }
  };

  const resetForm = () => {
    setForm({
      milestone_type: 'action_streak',
      action_id: actions[0]?.id || '',
      streak_days: 7,
      points_threshold: 50,
      badge_name: '',
      badge_icon: 'award',
      celebration_message: '',
      health_benefit: '',
      celebration_type: 'confetti',
      celebration_gif_url: ''
    });
    setEditing(null);
  };

  const openEdit = (milestone) => {
    setForm({
      milestone_type: milestone.milestone_type || 'action_streak',
      action_id: milestone.action_id || '',
      streak_days: milestone.streak_days || 7,
      points_threshold: milestone.points_threshold || 50,
      badge_name: milestone.badge_name,
      badge_icon: milestone.badge_icon,
      celebration_message: milestone.celebration_message,
      health_benefit: milestone.health_benefit,
      celebration_type: milestone.celebration_type || 'confetti',
      celebration_gif_url: milestone.celebration_gif_url || ''
    });
    setEditing(milestone.id);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation based on milestone type
    if (!form.badge_name || !form.celebration_message) {
      toast.error('Please fill in badge name and celebration message');
      return;
    }
    
    if (form.milestone_type === 'action_streak' && !form.action_id) {
      toast.error('Please select an action for action streak milestone');
      return;
    }
    
    if ((form.milestone_type === 'action_streak' || form.milestone_type === 'checkin_streak') && !form.streak_days) {
      toast.error('Please enter streak days');
      return;
    }
    
    if (form.milestone_type === 'monthly_points' && !form.points_threshold) {
      toast.error('Please enter points threshold');
      return;
    }
    
    // If celebration type is gif, require a gif URL
    if (form.celebration_type === 'gif' && !form.celebration_gif_url) {
      toast.error('Please select a celebration GIF');
      return;
    }

    setSaving(true);
    try {
      // Build payload based on milestone type
      const payload = {
        milestone_type: form.milestone_type,
        badge_name: form.badge_name,
        badge_icon: form.badge_icon,
        celebration_message: form.celebration_message,
        health_benefit: form.health_benefit,
        celebration_type: form.celebration_type,
        celebration_gif_url: form.celebration_gif_url || null
      };
      
      // Add type-specific fields
      if (form.milestone_type === 'action_streak') {
        payload.action_id = form.action_id;
        payload.streak_days = form.streak_days;
      } else if (form.milestone_type === 'checkin_streak') {
        payload.streak_days = form.streak_days;
      } else if (form.milestone_type === 'monthly_points') {
        payload.points_threshold = form.points_threshold;
      }
      // perfect_day and perfect_month don't need extra fields
      
      if (editing) {
        await apiClient.put(`/milestones/${editing}`, payload);
        toast.success('Milestone updated');
      } else {
        await apiClient.post('/milestones', payload);
        toast.success('Milestone created');
      }
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save milestone');
    } finally {
      setSaving(false);
    }
  };

  const previewCelebration = () => {
    if (form.celebration_type === 'gif' && form.celebration_gif_url) {
      // For GIF, just show a toast with preview info
      toast.success('GIF celebration will show the selected image full-screen!');
    } else {
      triggerCelebration(form.celebration_type);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this milestone?')) return;
    
    try {
      await apiClient.delete(`/milestones/${id}`);
      toast.success('Milestone deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete milestone');
    }
  };

  const getActionName = (actionId) => {
    if (actionId === 'any') return 'Any Action (General Check-in)';
    return actions.find(a => a.id === actionId)?.name || 'Unknown Action';
  };

  const getMilestoneTypeLabel = (type) => {
    const found = MILESTONE_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };

  const getMilestoneDescription = (milestone) => {
    const type = milestone.milestone_type || 'action_streak';
    switch (type) {
      case 'action_streak':
        if (milestone.action_id === 'any') {
          return `${milestone.streak_days} consecutive days checking in (any action)`;
        }
        return `${milestone.streak_days} days of ${getActionName(milestone.action_id)}`;
      case 'perfect_day':
        return 'Complete all 10 actions in one day';
      case 'checkin_streak':
        return `${milestone.streak_days} consecutive days checked in`;
      case 'monthly_points':
        return `Reach ${milestone.points_threshold} points in a month`;
      case 'perfect_month':
        return 'All 280 points in a month!';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900">MILESTONES</h1>
            <p className="text-stone-600 text-sm">Configure badge rewards for achievements</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold" data-testid="add-milestone-btn">
                <Plus size={18} className="mr-2" />
                Add Milestone
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-stone-200 text-stone-900 max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">
                  {editing ? 'Edit Milestone' : 'Create Milestone'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                
                {/* Milestone Type Selection */}
                <div>
                  <Label className="text-[#EFB342]">Milestone Type *</Label>
                  <Select value={form.milestone_type} onValueChange={(v) => setForm({ ...form, milestone_type: v })}>
                    <SelectTrigger className="bg-stone-50 border-stone-200 mt-1" data-testid="milestone-type-select">
                      <SelectValue placeholder="Select milestone type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-stone-200">
                      {MILESTONE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex flex-col">
                            <span>{type.label}</span>
                            <span className="text-xs text-stone-500">{type.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Dynamic fields based on milestone type */}
                {form.milestone_type === 'action_streak' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Action *</Label>
                      <Select value={form.action_id} onValueChange={(v) => setForm({ ...form, action_id: v })}>
                        <SelectTrigger className="bg-stone-50 border-stone-200 mt-1" data-testid="milestone-action-select">
                          <SelectValue placeholder="Select action" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-stone-200">
                          {/* Any Action option */}
                          <SelectItem value="any" className="border-b border-stone-300 mb-1">
                            <span className="font-medium text-[#EFB342]">✨ Any Action (General Check-in)</span>
                          </SelectItem>
                          {actions.map(a => (
                            <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {form.action_id === 'any' && (
                        <p className="text-xs text-stone-500 mt-1">
                          Awards badge when user checks in for ANY action(s) X days in a row
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>Streak Days *</Label>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={form.streak_days}
                        onChange={(e) => setForm({ ...form, streak_days: parseInt(e.target.value) || 1 })}
                        className="bg-stone-50 border-stone-200 mt-1"
                        data-testid="milestone-streak-input"
                      />
                    </div>
                  </div>
                )}
                
                {form.milestone_type === 'checkin_streak' && (
                  <div>
                    <Label>Consecutive Days *</Label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={form.streak_days}
                      onChange={(e) => setForm({ ...form, streak_days: parseInt(e.target.value) || 1 })}
                      placeholder="e.g., 7 for a week streak"
                      className="bg-stone-50 border-stone-200 mt-1"
                    />
                    <p className="text-xs text-stone-500 mt-1">User must check in (at least 1 point) for this many consecutive days</p>
                  </div>
                )}
                
                {form.milestone_type === 'monthly_points' && (
                  <div>
                    <Label>Points Threshold *</Label>
                    <Input
                      type="number"
                      min="1"
                      max="280"
                      value={form.points_threshold}
                      onChange={(e) => setForm({ ...form, points_threshold: parseInt(e.target.value) || 50 })}
                      placeholder="e.g., 50, 100, 150, 200, 250"
                      className="bg-stone-50 border-stone-200 mt-1"
                    />
                    <p className="text-xs text-stone-500 mt-1">Awarded when user reaches this many points in a single month (max 280)</p>
                  </div>
                )}
                
                {form.milestone_type === 'perfect_day' && (
                  <div className="bg-stone-50 rounded-lg p-3 text-sm text-stone-600">
                    <Star className="inline mr-2 text-yellow-500" size={16} />
                    This milestone is awarded when a user completes all 10 actions in a single day.
                  </div>
                )}
                
                {form.milestone_type === 'perfect_month' && (
                  <div className="bg-stone-50 rounded-lg p-3 text-sm text-stone-600">
                    <Trophy className="inline mr-2 text-yellow-500" size={16} />
                    The BIG one! Awarded when a user earns all 280 possible points in a month (10 points × 28 days).
                  </div>
                )}
                
                <div className="border-t border-stone-200 pt-4">
                  <Label className="text-stone-600 text-xs uppercase tracking-wider">Badge Details</Label>
                </div>
                
                <div>
                  <Label>Badge Name *</Label>
                  <Input
                    value={form.badge_name}
                    onChange={(e) => setForm({ ...form, badge_name: e.target.value })}
                    placeholder="Hydration Hero"
                    className="bg-stone-50 border-stone-200 mt-1"
                    data-testid="milestone-badge-name-input"
                  />
                </div>
                
                {/* Badge Icon Selector */}
                <div>
                  <Label>Badge Icon *</Label>
                  <Select value={form.badge_icon} onValueChange={(v) => setForm({ ...form, badge_icon: v })}>
                    <SelectTrigger className="bg-stone-50 border-stone-200 mt-1" data-testid="milestone-icon-select">
                      <SelectValue placeholder="Select icon" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-stone-200 max-h-60">
                      {BADGE_ICONS.map(icon => {
                        const IconComponent = icon.icon;
                        return (
                          <SelectItem key={icon.value} value={icon.value}>
                            <div className="flex items-center gap-2">
                              <IconComponent size={16} className="text-[#EFB342]" />
                              <span>{icon.label}</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Celebration Message *</Label>
                  <Textarea
                    value={form.celebration_message}
                    onChange={(e) => setForm({ ...form, celebration_message: e.target.value })}
                    placeholder="You've crushed your daily water goal for 7 days straight!"
                    className="bg-stone-50 border-stone-200 mt-1"
                    rows={2}
                    data-testid="milestone-celebration-input"
                  />
                </div>
                <div>
                  <Label>Health Benefit Message</Label>
                  <Textarea
                    value={form.health_benefit}
                    onChange={(e) => setForm({ ...form, health_benefit: e.target.value })}
                    placeholder="You've just increased your metabolism by 30% this week!"
                    className="bg-stone-50 border-stone-200 mt-1"
                    rows={2}
                    data-testid="milestone-health-benefit-input"
                  />
                </div>
                
                {/* Celebration Type Selection */}
                <div className="border-t border-stone-200 pt-4 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-[#EFB342]">Celebration Style</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={previewCelebration}
                      className="border-stone-300 text-stone-700 hover:bg-stone-100"
                    >
                      <PartyPopper size={14} className="mr-1" /> Preview
                    </Button>
                  </div>
                  <Select 
                    value={form.celebration_type} 
                    onValueChange={(v) => setForm({ ...form, celebration_type: v, celebration_gif_url: v === 'gif' ? form.celebration_gif_url : '' })}
                  >
                    <SelectTrigger className="bg-stone-50 border-stone-200" data-testid="milestone-celebration-type-select">
                      <SelectValue placeholder="Select celebration type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-stone-200">
                      {CELEBRATION_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex flex-col">
                            <span>{type.label}</span>
                            <span className="text-xs text-stone-500">{type.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* GIF Selection (only shown when celebration_type is 'gif') */}
                {form.celebration_type === 'gif' && (
                  <div className="space-y-3">
                    <Label>Select Celebration GIF *</Label>
                    <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 bg-stone-50 rounded-lg border border-stone-200">
                      {CELEBRATION_GIFS.map(gif => (
                        <button
                          key={gif.id}
                          type="button"
                          onClick={() => setForm({ ...form, celebration_gif_url: gif.url })}
                          className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                            form.celebration_gif_url === gif.url 
                              ? 'border-[#EFB342] ring-2 ring-[#EFB342]/50' 
                              : 'border-stone-300 hover:border-stone-400'
                          }`}
                        >
                          <img 
                            src={gif.preview} 
                            alt={gif.name} 
                            className="w-full h-16 object-cover"
                          />
                          {form.celebration_gif_url === gif.url && (
                            <div className="absolute inset-0 bg-[#EFB342]/20 flex items-center justify-center">
                              <span className="text-stone-900 text-lg">✓</span>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                    <div>
                      <Label className="text-xs text-stone-500">Or paste a custom GIF URL:</Label>
                      <Input
                        value={form.celebration_gif_url}
                        onChange={(e) => setForm({ ...form, celebration_gif_url: e.target.value })}
                        placeholder="https://media.giphy.com/media/.../giphy.gif"
                        className="bg-stone-50 border-stone-200 mt-1 text-sm"
                      />
                    </div>
                    {form.celebration_gif_url && (
                      <div className="bg-stone-50 rounded-lg p-2 border border-stone-200">
                        <p className="text-xs text-stone-500 mb-2">Preview:</p>
                        <img 
                          src={form.celebration_gif_url} 
                          alt="Selected GIF" 
                          className="max-h-32 mx-auto rounded"
                        />
                      </div>
                    )}
                  </div>
                )}
                
                <Button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold"
                  data-testid="milestone-submit-btn"
                >
                  {saving ? <Loader2 className="animate-spin" size={18} /> : editing ? 'Update' : 'Create'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Milestones List */}
        <div className="space-y-4">
          {milestones.length > 0 ? (
            milestones.map((milestone, index) => {
              const MilestoneTypeIcon = MILESTONE_TYPES.find(t => t.value === (milestone.milestone_type || 'action_streak'))?.icon || Flame;
              // Get the actual badge icon the user selected
              const BadgeIconData = BADGE_ICONS.find(i => i.value === milestone.badge_icon);
              const BadgeIcon = BadgeIconData?.icon || Award;
              
              return (
                <div
                  key={milestone.id}
                  className="bg-white border border-stone-200 rounded-2xl p-6"
                  data-testid={`milestone-${milestone.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Reorder Buttons */}
                    <div className="flex flex-col gap-1 pt-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => moveMilestone(index, 'up')}
                        disabled={index === 0 || reordering}
                        className={`h-7 w-7 ${index === 0 ? 'text-stone-400' : 'text-stone-600 hover:text-stone-900 hover:bg-stone-200'}`}
                        data-testid={`move-up-${milestone.id}`}
                      >
                        <ChevronUp size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => moveMilestone(index, 'down')}
                        disabled={index === milestones.length - 1 || reordering}
                        className={`h-7 w-7 ${index === milestones.length - 1 ? 'text-stone-400' : 'text-stone-600 hover:text-stone-900 hover:bg-stone-200'}`}
                        data-testid={`move-down-${milestone.id}`}
                      >
                        <ChevronDown size={18} />
                      </Button>
                    </div>
                    
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-14 h-14 bg-[#EFB342] hexagon flex items-center justify-center flex-shrink-0">
                        <BadgeIcon className="text-black" size={28} />
                      </div>
                      <div>
                        <div className="flex items-center flex-wrap gap-2 mb-1">
                          <h3 className="font-semibold text-stone-900 text-lg">{milestone.badge_name}</h3>
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full font-medium">
                            {getMilestoneTypeLabel(milestone.milestone_type || 'action_streak')}
                          </span>
                          {milestone.celebration_type && (
                            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full font-medium">
                              {milestone.celebration_type === 'gif' ? '🖼️ GIF' : CELEBRATION_TYPES.find(t => t.value === milestone.celebration_type)?.label || milestone.celebration_type}
                            </span>
                          )}
                        </div>
                        <p className="text-stone-600 text-sm mb-2">
                          {getMilestoneDescription(milestone)}
                        </p>
                        <p className="text-stone-600 text-sm">"{milestone.celebration_message}"</p>
                        {milestone.health_benefit && (
                          <p className="text-green-700 text-sm mt-2 italic">{milestone.health_benefit}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(milestone)}
                        className="text-stone-600 hover:text-stone-900"
                        data-testid={`edit-milestone-${milestone.id}`}
                      >
                        <Pencil size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(milestone.id)}
                        className="text-stone-600 hover:text-red-500"
                        data-testid={`delete-milestone-${milestone.id}`}
                      >
                        <Trash2 size={18} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-16 bg-white border border-stone-200 rounded-2xl">
              <Award className="mx-auto text-stone-400 mb-4" size={48} />
              <p className="text-stone-500">No milestones configured</p>
              <p className="text-stone-400 text-sm">Set up achievements to motivate users</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminMilestones;
